import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../apiBase';
import { getActiveSchoolId } from '../utils/activeSchool';
import { SUPPORTED_LANGUAGES } from '../i18n/languages';
import {
    COUNTRIES, getCountryByCode, getPaymentProviders, getCurrencySymbol, applyCountryToForm,
} from '../data/countries';
import CountryRequestModal from '../components/orgSettings/CountryRequestModal';

const ALL_CURRENCIES = [...new Set(COUNTRIES.map(c => c.currency))];

const TAX_SYSTEMS = [
    { value: 'OSN', labelKey: 'taxes.osn' },
    { value: 'USN6', labelKey: 'taxes.usn6' },
    { value: 'USN15', labelKey: 'taxes.usn15' },
    { value: 'Patent', labelKey: 'taxes.patent' },
    { value: 'ENVD', labelKey: 'taxes.envd' },
    { value: 'None', labelKey: 'taxes.none' },
];

const CURRENCIES = ALL_CURRENCIES;
const TIMEZONES = [
    'Europe/Moscow', 'Europe/Minsk', 'Asia/Almaty', 'Asia/Tashkent',
    'Europe/Berlin', 'Europe/London', 'America/New_York', 'UTC',
];

const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' };
const fieldStyle = { marginBottom: '16px' };

function emptyForm() {
    return {
        region: 'Russia',
        countryCode: 'RU',
        timeZone: 'Europe/Moscow',
        defaultLanguage: 'ru',
        defaultCurrency: 'RUB',
        paymentProvider: 'None',
        platformFeePercent: 2,
        yooKassaShopId: '',
        yooKassaSecretKey: '',
        yooKassaConnected: false,
        stripeAccountId: '',
        stripePublishableKey: '',
        stripeSecretKey: '',
        stripeConnected: false,
        legalName: '',
        shortName: '',
        legalAddress: '',
        actualAddress: '',
        directorName: '',
        directorTitle: '',
        phone: '',
        email: '',
        website: '',
        logoUrl: '',
        inn: '',
        kpp: '',
        ogrn: '',
        okpo: '',
        bankName: '',
        bankBik: '',
        bankAccount: '',
        bankCorAccount: '',
        bankSwift: '',
        bankIban: '',
        bankAddress: '',
        registrationNumber: '',
        vatNumber: '',
        taxSystem: 'None',
        isVatPayer: false,
        vatRatePercent: 20,
        invoicePrefix: 'SP',
        invoiceStartNumber: 1,
        privacyPolicyUrl: '',
        termsUrl: '',
        offerUrl: '',
        cookiePolicyUrl: '',
        refundPolicyUrl: '',
        hasLegalEntity: false,
        hasEcp: false,
        hasKassa: false,
        kassaRegistered: false,
        hasOfd: false,
        hasYooKassaContract: false,
        kassaOnboardingStatus: 'NotStarted',
    };
}

function mapApiToForm(data) {
    const base = emptyForm();
    if (!data) return base;
    return {
        ...base,
        region: data.region ?? base.region,
        countryCode: data.countryCode ?? base.countryCode,
        timeZone: data.timeZone ?? base.timeZone,
        defaultLanguage: data.defaultLanguage ?? base.defaultLanguage,
        defaultCurrency: data.defaultCurrency ?? base.defaultCurrency,
        paymentProvider: data.paymentProvider ?? base.paymentProvider,
        platformFeePercent: data.platformFeePercent ?? base.platformFeePercent,
        yooKassaShopId: data.yooKassaShopId ?? '',
        yooKassaSecretKey: '',
        yooKassaConnected: !!data.yooKassaConnected,
        stripeAccountId: data.stripeAccountId ?? '',
        stripePublishableKey: data.stripePublishableKey ?? '',
        stripeSecretKey: '',
        stripeConnected: !!data.stripeConnected,
        legalName: data.legalName ?? '',
        shortName: data.shortName ?? '',
        legalAddress: data.legalAddress ?? '',
        actualAddress: data.actualAddress ?? '',
        directorName: data.directorName ?? '',
        directorTitle: data.directorTitle ?? '',
        phone: data.phone ?? '',
        email: data.email ?? '',
        website: data.website ?? '',
        logoUrl: data.logoUrl ?? '',
        inn: data.inn ?? '',
        kpp: data.kpp ?? '',
        ogrn: data.ogrn ?? '',
        okpo: data.okpo ?? '',
        bankName: data.bankName ?? '',
        bankBik: data.bankBik ?? '',
        bankAccount: data.bankAccount ?? '',
        bankCorAccount: data.bankCorAccount ?? '',
        bankSwift: data.bankSwift ?? '',
        bankIban: data.bankIban ?? '',
        bankAddress: data.bankAddress ?? '',
        registrationNumber: data.registrationNumber ?? '',
        vatNumber: data.vatNumber ?? '',
        taxSystem: data.taxSystem ?? base.taxSystem,
        isVatPayer: !!data.isVatPayer,
        vatRatePercent: data.vatRatePercent ?? base.vatRatePercent,
        invoicePrefix: data.invoicePrefix ?? base.invoicePrefix,
        invoiceStartNumber: data.invoiceStartNumber ?? base.invoiceStartNumber,
        privacyPolicyUrl: data.privacyPolicyUrl ?? '',
        termsUrl: data.termsUrl ?? '',
        offerUrl: data.offerUrl ?? '',
        cookiePolicyUrl: data.cookiePolicyUrl ?? '',
        refundPolicyUrl: data.refundPolicyUrl ?? '',
        hasLegalEntity: !!data.hasLegalEntity,
        hasEcp: !!data.hasEcp,
        hasKassa: !!data.hasKassa,
        kassaRegistered: !!data.kassaRegistered,
        hasOfd: !!data.hasOfd,
        hasYooKassaContract: !!data.hasYooKassaContract,
        kassaOnboardingStatus: data.kassaOnboardingStatus ?? base.kassaOnboardingStatus,
        _yooMasked: data.yooKassaSecretMasked ?? null,
        _stripeMasked: data.stripeSecretMasked ?? null,
    };
}

function buildPutPayload(form) {
    const payload = { ...form };
    delete payload._yooMasked;
    delete payload._stripeMasked;
    if (!payload.yooKassaSecretKey?.trim()) delete payload.yooKassaSecretKey;
    else if (payload.yooKassaSecretKey.includes('****')) delete payload.yooKassaSecretKey;
    if (!payload.stripeSecretKey?.trim()) delete payload.stripeSecretKey;
    else if (payload.stripeSecretKey.includes('****')) delete payload.stripeSecretKey;
    delete payload.yooKassaConnected;
    delete payload.stripeConnected;
    return payload;
}

function schoolQuery() {
    const id = getActiveSchoolId();
    return id ? `?schoolId=${id}` : '';
}

export default function SchoolOrganizationSettingsPage() {
    const { t, i18n } = useTranslation(['orgSettings', 'common']);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState('region');
    const [form, setForm] = useState(emptyForm);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewText, setPreviewText] = useState('');
    const [helpOpen, setHelpOpen] = useState(false);
    const [helpForm, setHelpForm] = useState({ contactName: '', contactPhone: '', message: '' });
    const [testingYoo, setTestingYoo] = useState(false);
    const [testingStripe, setTestingStripe] = useState(false);
    const [testMessage, setTestMessage] = useState('');
    const [countryFilter, setCountryFilter] = useState('');
    const [showCountryRequest, setShowCountryRequest] = useState(false);

    const token = localStorage.getItem('token');
    const api = useMemo(() => axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${token}` },
    }), [token]);

    const basePath = '/school/organization-settings';

    const selectedCountry = useMemo(
        () => getCountryByCode(form.countryCode),
        [form.countryCode],
    );
    const paymentProviders = useMemo(
        () => getPaymentProviders(form.countryCode),
        [form.countryCode],
    );

    const filteredCountries = useMemo(() => {
        const q = countryFilter.trim().toLowerCase();
        if (!q) return COUNTRIES;
        return COUNTRIES.filter(c =>
            c.code.toLowerCase().includes(q)
            || c.nameRu.toLowerCase().includes(q)
            || c.nameEn.toLowerCase().includes(q));
    }, [countryFilter]);

    const visibleTabs = useMemo(() => {
        const region = selectedCountry?.region ?? form.region;
        const tabs = [
            { key: 'region', label: t('orgSettings:tab.region') },
            { key: 'requisites', label: t('orgSettings:tab.requisites') },
            { key: 'payments', label: t('orgSettings:tab.payments') },
        ];
        if (region === 'Russia' || region === 'CIS' || region === 'International') {
            tabs.push({ key: 'taxes', label: t('orgSettings:tab.taxes') });
        }
        if (region === 'Russia') {
            tabs.push({ key: 'kassaOnboarding', label: t('orgSettings:tab.kassaOnboarding') });
        }
        return tabs;
    }, [selectedCountry, form.region, t]);

    useEffect(() => {
        if (!visibleTabs.some(x => x.key === tab)) setTab('region');
    }, [visibleTabs, tab]);

    const kassaDone = useMemo(() => {
        let n = 0;
        if (form.hasLegalEntity) n++;
        if (form.hasEcp) n++;
        if (form.hasKassa && form.kassaRegistered) n++;
        else if (form.hasKassa) n += 0.5;
        if (form.hasOfd) n++;
        if (form.hasYooKassaContract) n++;
        return Math.min(5, Math.round(n));
    }, [form]);

    const showYoo = paymentProviders.includes('yookassa');
    const showStripe = paymentProviders.includes('stripe');
    const region = selectedCountry?.region ?? form.region;
    const showBankRu = region !== 'International';
    const showBankIntl = region !== 'Russia';

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.get(`${basePath}${schoolQuery()}`);
            setForm(mapApiToForm(res.data));
        } catch (err) {
            setError(err.response?.data?.message || t('orgSettings:loadError'));
        }
        setLoading(false);
    }, [api, t]);

    useEffect(() => { load(); }, [load]);

    async function handleSave(e) {
        if (e?.preventDefault) e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const res = await api.put(`${basePath}${schoolQuery()}`, buildPutPayload(form));
            setForm(mapApiToForm(res.data));
            setSuccess(t('orgSettings:saved'));
        } catch (err) {
            setError(err.response?.data?.message || t('orgSettings:loadError'));
        }
        setSaving(false);
    }

    async function openRequisitesPreview() {
        try {
            const res = await api.get(`${basePath}/requisites${schoolQuery()}`);
            setPreviewText(JSON.stringify(res.data, null, 2));
            setPreviewOpen(true);
        } catch (err) {
            setError(err.response?.data?.message || t('orgSettings:loadError'));
        }
    }

    async function testYooKassa() {
        setTestingYoo(true);
        setTestMessage('');
        try {
            const res = await api.post(`${basePath}/test-yookassa${schoolQuery()}`, {
                shopId: form.yooKassaShopId || undefined,
                secretKey: form.yooKassaSecretKey || undefined,
            });
            if (res.data?.success) {
                setTestMessage(t('orgSettings:payments.testOk', { shopName: res.data.shopName || 'OK' }));
                setForm(f => ({ ...f, yooKassaConnected: true }));
            } else {
                setTestMessage(t('orgSettings:payments.testFail', { error: res.data?.error || '?' }));
            }
        } catch (err) {
            setTestMessage(t('orgSettings:payments.testFail', { error: err.response?.data?.error || err.message }));
        }
        setTestingYoo(false);
    }

    async function testStripe() {
        setTestingStripe(true);
        setTestMessage('');
        try {
            const res = await api.post(`${basePath}/test-stripe${schoolQuery()}`);
            if (res.data?.success) {
                setTestMessage(t('orgSettings:payments.testOk', { shopName: t('orgSettings:payments.stripe', { defaultValue: 'International cards' }) }));
            } else {
                setTestMessage(t('orgSettings:payments.testFail', { error: res.data?.error || '?' }));
            }
        } catch (err) {
            setTestMessage(t('orgSettings:payments.testFail', { error: err.message }));
        }
        setTestingStripe(false);
    }

    async function submitHelp(e) {
        e.preventDefault();
        try {
            await api.post(`${basePath}/onboarding-help${schoolQuery()}`, {
                schoolId: getActiveSchoolId(),
                contactName: helpForm.contactName,
                contactPhone: helpForm.contactPhone,
                message: helpForm.message,
                checklistJson: JSON.stringify({
                    hasLegalEntity: form.hasLegalEntity,
                    hasEcp: form.hasEcp,
                    hasKassa: form.hasKassa,
                    kassaRegistered: form.kassaRegistered,
                    hasOfd: form.hasOfd,
                    hasYooKassaContract: form.hasYooKassaContract,
                }),
            });
            alert(t('orgSettings:kassaOnboarding.helpRequestSent'));
            setHelpOpen(false);
            setHelpForm({ contactName: '', contactPhone: '', message: '' });
        } catch (err) {
            setError(err.response?.data?.message || t('orgSettings:loadError'));
        }
    }

    function handleCountryChange(code) {
        setForm(prev => applyCountryToForm(prev, code));
    }

    function setField(key, value) {
        setForm(prev => ({ ...prev, [key]: value }));
    }

    function renderField(key, labelKey, type = 'text') {
        return (
            <div style={fieldStyle} key={key}>
                <label style={labelStyle}>{t(`orgSettings:requisites.${labelKey}`)}</label>
                <input
                    className="sp-input"
                    type={type}
                    value={form[key] ?? ''}
                    onChange={e => setField(key, e.target.value)}
                />
            </div>
        );
    }

    const invoicePreview = t('orgSettings:taxes.invoicePreview', {
        prefix: form.invoicePrefix || 'SP',
        year: new Date().getFullYear(),
        number: String(form.invoiceStartNumber || 1).padStart(4, '0'),
    });

    if (loading) {
        return (
            <OwnerPageLayout title={t('orgSettings:pageTitle')} maxWidth={800}>
                <div className="sp-loading-page">{t('common:loading')}</div>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout title={t('orgSettings:pageTitle')} maxWidth={800}>
            {error && (
                <div style={{
                    background: '#fdecea', color: '#c0392b', padding: '12px 16px',
                    borderRadius: '10px', marginBottom: '16px', fontSize: '14px',
                }}>
                    {error}
                </div>
            )}
            {success && (
                <div style={{
                    background: '#e8f5e9', color: '#2e7d32', padding: '12px 16px',
                    borderRadius: '10px', marginBottom: '16px', fontSize: '14px',
                }}>
                    {success}
                </div>
            )}

            <div className="sp-tabs" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                {visibleTabs.map(item => (
                    <button
                        key={item.key}
                        type="button"
                        className={`sp-tab${tab === item.key ? ' sp-tab--active' : ''}`}
                        onClick={() => setTab(item.key)}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            {tab === 'region' && (
                <div className="sp-panel">
                    <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>{t('orgSettings:region.title')}</h3>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>{t('orgSettings:region.country')}</label>
                        <input
                            className="sp-input"
                            value={countryFilter}
                            onChange={e => setCountryFilter(e.target.value)}
                            placeholder={t('common:placeholder.search', { defaultValue: 'Поиск...' })}
                            style={{ marginBottom: 8 }}
                        />
                        <select
                            className="sp-input"
                            value={form.countryCode}
                            onChange={e => handleCountryChange(e.target.value)}
                            size={Math.min(8, filteredCountries.length + 1)}
                            style={{ minHeight: 120 }}
                        >
                            {filteredCountries.map(c => (
                                <option key={c.code} value={c.code}>
                                    {`${c.flag} ${i18n.language === 'ru' ? c.nameRu : c.nameEn} (${c.code})`}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className="sp-btn sp-btn--ghost"
                            style={{ marginTop: 8 }}
                            onClick={() => setShowCountryRequest(true)}
                        >
                            {t('orgSettings:countryNotFound')}
                        </button>
                    </div>
                    {selectedCountry && (
                        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                            {t('orgSettings:region.autoRegion')}: {selectedCountry.region}
                            {' · '}
                            {paymentProviders.map((p) => (p === 'yookassa' ? 'ЮKassa' : t('orgSettings:payments.stripe', { defaultValue: 'International cards' }))).join(' / ')}
                        </p>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('orgSettings:region.currency')}</label>
                            <select
                                className="sp-input"
                                value={form.defaultCurrency}
                                onChange={e => setField('defaultCurrency', e.target.value)}
                            >
                                {CURRENCIES.map(c => (
                                    <option key={c} value={c}>{getCurrencySymbol(c)} {c}</option>
                                ))}
                            </select>
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('orgSettings:region.timezone')}</label>
                            <select
                                className="sp-input"
                                value={form.timeZone}
                                onChange={e => setField('timeZone', e.target.value)}
                            >
                                {TIMEZONES.map(z => <option key={z} value={z}>{z}</option>)}
                            </select>
                        </div>
                    </div>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>{t('orgSettings:region.defaultLanguage')}</label>
                        <select
                            className="sp-input"
                            value={form.defaultLanguage}
                            onChange={e => setField('defaultLanguage', e.target.value)}
                        >
                            {SUPPORTED_LANGUAGES.map(lang => (
                                <option key={lang.code} value={lang.code}>
                                    {lang.nativeLabel || lang.nativeName || lang.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <SaveBar saving={saving} onSave={handleSave} label={t('orgSettings:save')} />
                </div>
            )}

            {tab === 'requisites' && (
                <div className="sp-panel">
                    <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>{t('orgSettings:requisites.title')}</h3>
                    <p style={{ fontSize: '13px', color: '#64748b', marginBottom: 16 }}>{t('orgSettings:requisites.general')}</p>
                    {renderField('legalName', 'legalName')}
                    {renderField('shortName', 'shortName')}
                    {renderField('legalAddress', 'legalAddress')}
                    {renderField('actualAddress', 'actualAddress')}
                    {renderField('phone', 'phone')}
                    {renderField('email', 'email', 'email')}
                    {renderField('website', 'website')}
                    {renderField('directorName', 'directorName')}
                    {renderField('directorTitle', 'directorTitle')}

                    {showBankRu && (
                        <>
                            <h4 style={{ margin: '20px 0 12px', fontSize: '14px' }}>{t('orgSettings:requisites.bankRu')}</h4>
                            {renderField('bankName', 'bankName')}
                            {renderField('bankBik', 'bik')}
                            {renderField('bankAccount', 'account')}
                            {renderField('bankCorAccount', 'corAccount')}
                            {renderField('inn', 'inn')}
                            {renderField('kpp', 'kpp')}
                            {renderField('ogrn', 'ogrn')}
                            {renderField('okpo', 'okpo')}
                        </>
                    )}

                    {showBankIntl && (
                        <>
                            <h4 style={{ margin: '20px 0 12px', fontSize: '14px' }}>{t('orgSettings:requisites.bankIntl')}</h4>
                            {renderField('bankSwift', 'swift')}
                            {renderField('bankIban', 'iban')}
                            {renderField('bankAddress', 'bankAddress')}
                            {renderField('registrationNumber', 'registrationNumber')}
                            {renderField('vatNumber', 'vatNumber')}
                        </>
                    )}

                    <h4 style={{ margin: '20px 0 12px', fontSize: '14px' }}>{t('orgSettings:requisites.legalDocs')}</h4>
                    {renderField('privacyPolicyUrl', 'privacyUrl')}
                    {renderField('termsUrl', 'termsUrl')}
                    {renderField('offerUrl', 'offerUrl')}
                    {renderField('cookiePolicyUrl', 'cookieUrl')}
                    {renderField('refundPolicyUrl', 'refundUrl')}

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                        <button type="button" className="sp-btn sp-btn--ghost" onClick={openRequisitesPreview}>
                            {t('orgSettings:requisites.preview')}
                        </button>
                    </div>
                    <SaveBar saving={saving} onSave={handleSave} label={t('orgSettings:save')} />
                </div>
            )}

            {tab === 'payments' && (
                <div className="sp-panel">
                    <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>{t('orgSettings:payments.title')}</h3>
                    <p style={{ fontSize: '13px', color: '#64748b', marginBottom: 12 }}>
                        {t('orgSettings:payments.platformFeeHint', { fee: form.platformFeePercent })}
                    </p>

                    {showYoo && (
                        <div style={{ marginBottom: 24, padding: 16, background: '#f8fafc', borderRadius: 12 }}>
                            <h4 style={{ margin: '0 0 12px' }}>{t('orgSettings:payments.yookassa')}</h4>
                            <span style={{
                                display: 'inline-block', marginBottom: 12, padding: '4px 12px', borderRadius: 20,
                                fontSize: 12, fontWeight: 700,
                                background: form.yooKassaConnected ? '#e8f5e9' : '#fff3e0',
                                color: form.yooKassaConnected ? '#2e7d32' : '#e65100',
                            }}>
                                {form.yooKassaConnected
                                    ? t('orgSettings:payments.connected')
                                    : t('orgSettings:payments.notConnected')}
                            </span>
                            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                                {t('orgSettings:payments.yookassaInstructions')}
                            </p>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>{t('orgSettings:payments.shopId')}</label>
                                <input
                                    className="sp-input"
                                    value={form.yooKassaShopId}
                                    onChange={e => setField('yooKassaShopId', e.target.value)}
                                />
                            </div>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>{t('orgSettings:payments.secretKey')}</label>
                                <input
                                    className="sp-input"
                                    type="password"
                                    value={form.yooKassaSecretKey}
                                    placeholder={form._yooMasked || t('orgSettings:payments.changeKey')}
                                    onChange={e => setField('yooKassaSecretKey', e.target.value)}
                                    autoComplete="off"
                                />
                            </div>
                            <button
                                type="button"
                                className="sp-btn sp-btn--primary"
                                disabled={testingYoo}
                                onClick={testYooKassa}
                            >
                                {testingYoo ? t('common:loading') : t('orgSettings:payments.testConnection')}
                            </button>
                        </div>
                    )}

                    {showStripe && (
                        <div style={{ marginBottom: 24, padding: 16, background: '#f8fafc', borderRadius: 12 }}>
                            <h4 style={{ margin: '0 0 12px' }}>{t('orgSettings:payments.stripe')}</h4>
                            <span style={{
                                display: 'inline-block', marginBottom: 12, padding: '4px 12px', borderRadius: 20,
                                fontSize: 12, fontWeight: 700,
                                background: form.stripeConnected ? '#e8f5e9' : '#fff3e0',
                                color: form.stripeConnected ? '#2e7d32' : '#e65100',
                            }}>
                                {form.stripeConnected
                                    ? t('orgSettings:payments.connected')
                                    : t('orgSettings:payments.notConnected')}
                            </span>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>{t('orgSettings:payments.accountId')}</label>
                                <input
                                    className="sp-input"
                                    value={form.stripeAccountId}
                                    onChange={e => setField('stripeAccountId', e.target.value)}
                                />
                            </div>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>{t('orgSettings:payments.publishableKey')}</label>
                                <input
                                    className="sp-input"
                                    value={form.stripePublishableKey}
                                    onChange={e => setField('stripePublishableKey', e.target.value)}
                                />
                            </div>
                            <div style={fieldStyle}>
                                <label style={labelStyle}>{t('orgSettings:payments.secretKey')}</label>
                                <input
                                    className="sp-input"
                                    type="password"
                                    value={form.stripeSecretKey}
                                    placeholder={form._stripeMasked || t('orgSettings:payments.changeKey')}
                                    onChange={e => setField('stripeSecretKey', e.target.value)}
                                    autoComplete="off"
                                />
                            </div>
                            <button
                                type="button"
                                className="sp-btn sp-btn--primary"
                                disabled={testingStripe}
                                onClick={testStripe}
                            >
                                {testingStripe ? t('common:loading') : t('orgSettings:payments.testConnection')}
                            </button>
                        </div>
                    )}

                    {testMessage && (
                        <p style={{ fontSize: 13, marginBottom: 12 }}>{testMessage}</p>
                    )}
                    <SaveBar saving={saving} onSave={handleSave} label={t('orgSettings:save')} />
                </div>
            )}

            {tab === 'taxes' && (
                <div className="sp-panel">
                    <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>{t('orgSettings:taxes.title')}</h3>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>{t('orgSettings:taxes.taxSystem')}</label>
                        <select
                            className="sp-input"
                            value={form.taxSystem}
                            onChange={e => setField('taxSystem', e.target.value)}
                        >
                            {(selectedCountry?.taxSystems || TAX_SYSTEMS.map(ts => ts.value)).map(ts => (
                                <option key={ts} value={ts}>{ts}</option>
                            ))}
                        </select>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, fontSize: 13 }}>
                        <input
                            type="checkbox"
                            checked={form.isVatPayer}
                            onChange={e => setField('isVatPayer', e.target.checked)}
                        />
                        {t('orgSettings:taxes.isVatPayer')}
                    </label>
                    {form.isVatPayer && (
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('orgSettings:taxes.vatRate')}</label>
                            {(selectedCountry?.vatRates?.length ?? 0) > 0 ? (
                                <select
                                    className="sp-input"
                                    value={form.vatRatePercent ?? ''}
                                    onChange={e => setField('vatRatePercent', parseFloat(e.target.value))}
                                >
                                    {selectedCountry.vatRates.map(rate => (
                                        <option key={rate} value={rate}>{rate}%</option>
                                    ))}
                                </select>
                            ) : (
                                <p style={{ fontSize: 13, color: '#64748b' }}>
                                    {selectedCountry?.vatNote || t('orgSettings:taxes.vatNoteFallback')}
                                </p>
                            )}
                        </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('orgSettings:taxes.invoicePrefix')}</label>
                            <input
                                className="sp-input"
                                value={form.invoicePrefix}
                                onChange={e => setField('invoicePrefix', e.target.value)}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('orgSettings:taxes.invoiceStartNumber')}</label>
                            <input
                                className="sp-input"
                                type="number"
                                min={1}
                                value={form.invoiceStartNumber}
                                onChange={e => setField('invoiceStartNumber', parseInt(e.target.value, 10) || 1)}
                            />
                        </div>
                    </div>
                    <p style={{ fontSize: 13, color: '#64748b' }}>{invoicePreview}</p>
                    <SaveBar saving={saving} onSave={handleSave} label={t('orgSettings:save')} />
                </div>
            )}

            {tab === 'kassaOnboarding' && (
                <div className="sp-panel">
                    <h3 style={{ margin: '0 0 8px', fontSize: '16px' }}>{t('orgSettings:kassaOnboarding.title')}</h3>
                    <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                        {t('orgSettings:kassaOnboarding.progress', { done: kassaDone, total: 5 })}
                    </p>
                    <div style={{
                        height: 8, background: 'var(--sp-gray-100)', borderRadius: 4,
                        marginBottom: 24, overflow: 'hidden',
                    }}>
                        <div style={{
                            width: `${(kassaDone / 5) * 100}%`, height: '100%',
                            background: '#EB3333', transition: 'width 0.2s',
                        }} />
                    </div>

                    {kassaDone >= 5 && (
                        <p style={{ color: '#2e7d32', fontWeight: 600, marginBottom: 16 }}>
                            {t('orgSettings:kassaOnboarding.allDone')}
                        </p>
                    )}

                    <KassaStep
                        title={t('orgSettings:kassaOnboarding.step1Title')}
                        question={t('orgSettings:kassaOnboarding.step1Question')}
                        noHint={t('orgSettings:kassaOnboarding.step1NoHint')}
                        value={form.hasLegalEntity}
                        onYes={() => setField('hasLegalEntity', true)}
                        onNo={() => setField('hasLegalEntity', false)}
                        t={t}
                    />
                    <KassaStep
                        title={t('orgSettings:kassaOnboarding.step2Title')}
                        question={t('orgSettings:kassaOnboarding.step2Question')}
                        noHint={t('orgSettings:kassaOnboarding.step2NoHint')}
                        value={form.hasEcp}
                        onYes={() => setField('hasEcp', true)}
                        onNo={() => setField('hasEcp', false)}
                        t={t}
                    />
                    <KassaStep
                        title={t('orgSettings:kassaOnboarding.step3Title')}
                        question={t('orgSettings:kassaOnboarding.step3Question')}
                        noHint={t('orgSettings:kassaOnboarding.step3NoHint')}
                        partialHint={t('orgSettings:kassaOnboarding.step3PartialHint')}
                        value={form.hasKassa ? (form.kassaRegistered ? true : 'partial') : false}
                        onYes={() => setForm(prev => ({ ...prev, hasKassa: true, kassaRegistered: true }))}
                        onNo={() => setForm(prev => ({ ...prev, hasKassa: false, kassaRegistered: false }))}
                        onPartial={() => setForm(prev => ({ ...prev, hasKassa: true, kassaRegistered: false }))}
                        showPartial
                        t={t}
                    />
                    <KassaStep
                        title={t('orgSettings:kassaOnboarding.step4Title')}
                        question={t('orgSettings:kassaOnboarding.step4Question')}
                        noHint={t('orgSettings:kassaOnboarding.step4NoHint')}
                        value={form.hasOfd}
                        onYes={() => setField('hasOfd', true)}
                        onNo={() => setField('hasOfd', false)}
                        t={t}
                    />
                    <KassaStep
                        title={t('orgSettings:kassaOnboarding.step5Title')}
                        question={t('orgSettings:kassaOnboarding.step5Question')}
                        noHint={t('orgSettings:kassaOnboarding.step5Hint')}
                        value={form.hasYooKassaContract}
                        onYes={() => setField('hasYooKassaContract', true)}
                        onNo={() => setField('hasYooKassaContract', false)}
                        t={t}
                    />

                    <p style={{ marginTop: 16, fontSize: 13 }}>{t('orgSettings:kassaOnboarding.needHelp')}</p>
                    <button type="button" className="sp-btn sp-btn--ghost" onClick={() => setHelpOpen(true)}>
                        {t('orgSettings:kassaOnboarding.helpRequest')}
                    </button>
                    <SaveBar saving={saving} onSave={handleSave} label={t('orgSettings:save')} />
                </div>
            )}

            <div style={{ marginTop: 8 }}>
                <button
                    type="button"
                    className="sp-btn sp-btn--primary"
                    disabled={saving}
                    onClick={handleSave}
                >
                    {saving ? t('common:loading') : t('orgSettings:save')}
                </button>
            </div>

            {previewOpen && (
                <Modal
                    title={t('orgSettings:requisites.previewTitle')}
                    onClose={() => setPreviewOpen(false)}
                >
                    <pre style={{
                        margin: 0, padding: 12, background: 'var(--sp-gray-50)',
                        borderRadius: 8, fontSize: 12, overflow: 'auto', maxHeight: '60vh',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                        {previewText}
                    </pre>
                </Modal>
            )}

            {helpOpen && (
                <Modal
                    title={t('orgSettings:kassaOnboarding.helpRequestTitle')}
                    onClose={() => setHelpOpen(false)}
                >
                    <form onSubmit={submitHelp}>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('orgSettings:kassaOnboarding.helpName')}</label>
                            <input
                                className="sp-input"
                                required
                                value={helpForm.contactName}
                                onChange={e => setHelpForm(h => ({ ...h, contactName: e.target.value }))}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('orgSettings:kassaOnboarding.helpPhone')}</label>
                            <input
                                className="sp-input"
                                required
                                value={helpForm.contactPhone}
                                onChange={e => setHelpForm(h => ({ ...h, contactPhone: e.target.value }))}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>{t('orgSettings:kassaOnboarding.helpMessage')}</label>
                            <textarea
                                className="sp-input"
                                rows={4}
                                value={helpForm.message}
                                onChange={e => setHelpForm(h => ({ ...h, message: e.target.value }))}
                            />
                        </div>
                        <button type="submit" className="sp-btn sp-btn--primary">
                            {t('orgSettings:kassaOnboarding.helpRequest')}
                        </button>
                    </form>
                </Modal>
            )}
            <CountryRequestModal open={showCountryRequest} onClose={() => setShowCountryRequest(false)} />
        </OwnerPageLayout>
    );
}

function SaveBar({ saving, onSave, label }) {
    return (
        <button
            type="button"
            className="sp-btn sp-btn--primary"
            disabled={saving}
            onClick={onSave}
            style={{ marginTop: 8 }}
        >
            {saving ? '…' : label}
        </button>
    );
}

function KassaStep({
    title, question, noHint, partialHint, value, onYes, onNo, onPartial, showPartial, t,
}) {
    return (
        <div style={{
            marginBottom: 20, padding: 16, borderRadius: 12,
            border: '1px solid var(--sp-gray-200)',
        }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '14px' }}>{title}</h4>
            <p style={{ margin: '0 0 12px', fontSize: 13 }}>{question}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button
                    type="button"
                    className={`sp-btn${value === true ? ' sp-btn--primary' : ''}`}
                    onClick={onYes}
                >
                    {t('orgSettings:kassaOnboarding.yes')}
                </button>
                <button
                    type="button"
                    className={`sp-btn${value === false ? ' sp-btn--primary' : ''}`}
                    onClick={onNo}
                >
                    {t('orgSettings:kassaOnboarding.no')}
                </button>
                {showPartial && (
                    <button
                        type="button"
                        className={`sp-btn${value === 'partial' ? ' sp-btn--primary' : ''}`}
                        onClick={onPartial}
                    >
                        {t('orgSettings:kassaOnboarding.hasButNotRegistered')}
                    </button>
                )}
            </div>
            {value === false && noHint && (
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 12, lineHeight: 1.5 }}>{noHint}</p>
            )}
            {value === 'partial' && partialHint && (
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 12, lineHeight: 1.5 }}>{partialHint}</p>
            )}
        </div>
    );
}

function Modal({ title, onClose, children }) {
    return (
        <div
            role="dialog"
            style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                background: 'rgba(0,0,0,0.45)', display: 'flex',
                alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
            onClick={onClose}
        >
            <div
                className="sp-panel"
                style={{ maxWidth: 560, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
                onClick={e => e.stopPropagation()}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: '16px' }}>{title}</h3>
                    <button type="button" className="sp-btn sp-btn--ghost" onClick={onClose}>×</button>
                </div>
                {children}
            </div>
        </div>
    );
}
